import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useIdentity } from "@/auth/identity";
import { toast } from "@/hooks/use-toast";
import { Calendar, ArrowLeft, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { tripService } from "@/services/api";
import { useDriver } from "@/context/DriverContext";
import BottomNavigation from "@/components/BottomNavigation";
import {
  canEditOrDeleteAvailabilityForDate,
  isTomorrowPastCancelCutoff,
} from "@/utils/dateUtils";
import type { DriverAvailability, GroupedAvailability } from "@/types/availability";
import { groupAvailabilitiesByDateAndOperation, readAvailabilitiesResponse } from "@/utils/groupAvailabilities";
import { getDriverOperations, isMultiOperation, operationNamesOf } from "@/utils/operations";
import { CutoffDialog } from "@/components/availability/CutoffDialog";
import { AvailabilityGroupCard } from "@/components/availability/AvailabilityGroupCard";

const MyAvailabilities = () => {
  const navigate = useNavigate();
  const { user } = useIdentity();
  const { clearLastFetchDate, isApiClientReady, userScope } = useDriver();

  const ops = useMemo(
    () => getDriverOperations(userScope ?? { operations: [], svcs: [] }),
    [userScope]
  );
  const multiOp = isMultiOperation(ops);
  const operationNames = useMemo(() => operationNamesOf(ops), [ops]);

  const [rows, setRows] = useState<DriverAvailability[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [availabilityToDelete, setAvailabilityToDelete] = useState<GroupedAvailability | null>(null);
  const [showCutoffDialog, setShowCutoffDialog] = useState(false);

  // Las filas se guardan en bruto y la agrupación se deriva en un useMemo (más
  // abajo): si fetchAvailabilities dependiera de operationNames, un scope que
  // llega después del mount dispara un segundo GET y un segundo "Cargando…".
  const fetchAvailabilities = useCallback(async () => {
    try {
      setIsLoading(true);
      // No necesitamos pasar driverId ya que /me/availabilities usa la autenticación del usuario
      const response = await tripService.getDriverAvailability();
      setRows(readAvailabilitiesResponse(response));
    } catch (error: any) {
      console.error("Error fetching availabilities:", error);
      setRows([]); // Asegurar que siempre sea un array
      toast({
        title: "Error",
        description: "No se pudieron cargar las disponibilidades",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.sub && isApiClientReady) {
      fetchAvailabilities();
    }
  }, [user?.sub, isApiClientReady, fetchAvailabilities]);

  // Agrupar las disponibilidades por (fecha, operación)
  const groupedAvailabilities = useMemo(
    () => groupAvailabilitiesByDateAndOperation(rows, operationNames),
    [rows, operationNames]
  );

  // Badge de operación: con una sola operación en scope pero filas que abarcan
  // más de una (dato huérfano de un scope viejo), igual hay que distinguirlas.
  const showOperationBadge = useMemo(
    () => multiOp || new Set(groupedAvailabilities.map((g) => g.operationId)).size > 1,
    [multiOp, groupedAvailabilities]
  );

  // Actualizar el tiempo cada minuto para re-evaluar las restricciones de tiempo
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Actualizar cada minuto

    return () => clearInterval(interval);
  }, []);

  const handleDeleteClick = (groupedAvailability: GroupedAvailability) => {
    if (isTomorrowPastCancelCutoff(groupedAvailability.date)) {
      setShowCutoffDialog(true);
      return;
    }
    setAvailabilityToDelete(groupedAvailability);
  };

  const handleDeleteConfirm = async () => {
    if (!availabilityToDelete) return;

    try {
      setIsDeleting(availabilityToDelete.availabilities[0].id);

      const ids = availabilityToDelete.availabilities.map((a) => a.id);
      await tripService.deleteAvailabilitiesBulk(ids);

      // Actualizar la lista local: sacar las filas borradas, la agrupación se recalcula sola
      setRows((prev) => prev.filter((r) => !ids.includes(r.id)));

      clearLastFetchDate();
      toast({
        title: "Éxito",
        description: "Disponibilidades eliminadas correctamente",
      });
      setAvailabilityToDelete(null);
    } catch (error: any) {
      console.error("Error deleting availabilities:", error);

      let errorMessage = "No se pudieron eliminar las disponibilidades";

      if (error.response?.status === 403) {
        const backendMessage = error.response?.data?.message;
        if (backendMessage) {
          errorMessage = backendMessage;
        } else {
          errorMessage =
            "No se puede eliminar la disponibilidad del día actual";
        }
      } else if (error.response?.status === 400) {
        const backendMessage = error.response?.data?.message;
        if (backendMessage) {
          errorMessage = backendMessage;
        }
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const navigateToEdit = (groupedAvailability: GroupedAvailability) => {
    const availabilityForEdit = {
      id: groupedAvailability.availabilities[0].id,
      date: groupedAvailability.date,
      svcs: groupedAvailability.svcs.map((svc) => svc.code),
      shift: groupedAvailability.shifts[0],
      shifts: groupedAvailability.shifts,
      vehicles: groupedAvailability.vehicles.map((vehicle) => vehicle.id),
      availabilities: groupedAvailability.availabilities,
      operationId: groupedAvailability.operationId,
    };

    navigate("/report-availability", {
      state: {
        editMode: true,
        availability: availabilityForEdit,
      },
    });
  };

  const handleEdit = (groupedAvailability: GroupedAvailability) => {
    if (isTomorrowPastCancelCutoff(groupedAvailability.date)) {
      setShowCutoffDialog(true);
      return;
    }
    navigateToEdit(groupedAvailability);
  };

  // Verificar si se puede editar/borrar una disponibilidad según la restricción de tiempo
  // currentTime fuerza re-render cada minuto para actualizar el estado de los botones
  // Usamos useMemo con currentTime como dependencia para que se re-evalúe cuando cambie el tiempo
  const canEditOrDelete = useMemo(() => {
    // Referenciar currentTime para que React re-renderice cuando cambie
    // El valor actual de currentTime se usa implícitamente por canEditOrDeleteAvailabilityForDate
    // que obtiene la hora actual internamente
    return (date: string): boolean => {
      return canEditOrDeleteAvailabilityForDate(date);
    };
  }, [currentTime]);

  return (
    <div className="min-h-screen bg-gray-100 pb-16">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center justify-between">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Mis Disponibilidades</h1>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/report-availability")}
          className="text-white"
        >
          <Plus className="h-5 w-5" />
        </Button>
      </header>

      {/* Content */}
      <main className="container mx-auto p-4">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-gray-600">Cargando disponibilidades...</div>
          </div>
        ) : !Array.isArray(groupedAvailabilities) ||
          groupedAvailabilities.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay disponibilidades
            </h3>
            <p className="text-gray-600 mb-4">
              Aún no has reportado ninguna disponibilidad
            </p>
            <Button onClick={() => navigate("/report-availability")}>
              <Plus className="h-4 w-4 mr-2" />
              Crear Disponibilidad
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {Array.isArray(groupedAvailabilities) &&
              groupedAvailabilities.map((groupedAvailability) => (
                <AvailabilityGroupCard
                  key={`${groupedAvailability.date}|${groupedAvailability.operationId}`}
                  group={groupedAvailability}
                  canEditOrDelete={canEditOrDelete(groupedAvailability.date)}
                  isDeleting={isDeleting === groupedAvailability.availabilities[0].id}
                  onEdit={() => handleEdit(groupedAvailability)}
                  onDelete={() => handleDeleteClick(groupedAvailability)}
                  showOperation={showOperationBadge}
                />
              ))}
          </div>
        )}
      </main>

      <BottomNavigation />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!availabilityToDelete}
        onOpenChange={(open) => {
          if (!open) {
            setAvailabilityToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar eliminación</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  ¿Está seguro de que desea eliminar todas las disponibilidades
                  del{" "}
                  <span className="notranslate" translate="no">
                    {availabilityToDelete?.formattedDate}
                  </span>
                  {multiOp && (
                    <>
                      {" "}para <span translate="no">{availabilityToDelete?.operationName}</span>
                    </>
                  )}
                  ?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cutoff dialog: baja bloqueada después de las 18:00 del día anterior */}
      <CutoffDialog open={showCutoffDialog} onOpenChange={setShowCutoffDialog} />
    </div>
  );
};

export default MyAvailabilities;
